/**
 * Live transcription: transcribe while the session is still running.
 *
 * A long session used to mean a long wait — the whole recording went to
 * Whisper in one call after the meeting, and anything over 25MB was rejected
 * outright. Instead we run a *second*, audio-only recorder that restarts every
 * couple of minutes, producing small self-contained clips that upload and get
 * transcribed as the session unfolds. By the time the chair adjourns, the
 * transcript is already written and the minutes draft starts immediately.
 */
import api from "./api";

const AUDIO_MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];
const SEGMENT_MS = 120000; // 2 minutes — small enough to transcribe in seconds

const pickMime = () => {
  if (typeof window === "undefined" || !window.MediaRecorder) return "";
  for (const m of AUDIO_MIMES) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch { /* Safari throws on odd strings */ }
  }
  return "";
};

const extFor = (mime = "") => {
  if (mime.includes("mp4")) return "m4a";
  if (mime.includes("mpeg")) return "mp3";
  return "weba";
};

/**
 * @returns handle with `stop()` (flushes the final clip) or null when this
 * browser/stream can't feed it. Never throws — transcription is a bonus, the
 * session recording is the contract.
 */
export function startLiveTranscription({ stream, meetingId, segmentMs = SEGMENT_MS }) {
  const source = stream;
  const audioTracks = source ? source.getAudioTracks() : [];
  if (!audioTracks.length || !window.MediaRecorder) return null;

  const mime = pickMime();
  const ext = extFor(mime);
  const audioOnly = new MediaStream(audioTracks);

  let index = 0;
  let startedAt = Date.now();
  let stopped = false;
  let disabled = false; // server said this plan doesn't transcribe
  let recorder = null;
  let timer = null;
  const pending = [];

  const upload = async (blob, segIndex, startSec) => {
    if (!blob || blob.size < 2048 || disabled) return;
    const form = new FormData();
    form.append("index", String(segIndex));
    form.append("start_sec", String(Math.round(startSec)));
    form.append("file", blob, `segment-${segIndex}.${ext}`);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/transcript-segment`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.skipped) disabled = true;
    } catch (e) {
      // A dropped clip costs a couple of minutes of transcript, never the session.
      console.warn("Live transcript segment failed", e?.response?.status || e?.message);
    }
  };

  const cycle = () => {
    if (stopped || disabled) return;
    try {
      const options = mime ? { mimeType: mime, audioBitsPerSecond: 48000 } : { audioBitsPerSecond: 48000 };
      recorder = new MediaRecorder(audioOnly, options);
    } catch (e) {
      console.warn("Live transcription unavailable", e);
      stopped = true;
      return;
    }
    const chunks = [];
    const segIndex = index++;
    const startSec = (Date.now() - startedAt) / 1000;
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    recorder.onstop = () => {
      const blob = chunks.length ? new Blob(chunks, { type: recorder.mimeType || mime || "audio/webm" }) : null;
      pending.push(upload(blob, segIndex, startSec));
      if (!stopped) cycle();
    };
    try {
      recorder.start();
      timer = setTimeout(() => {
        if (recorder && recorder.state === "recording") recorder.stop();
      }, segmentMs);
    } catch (e) {
      console.warn("Live transcription could not start", e);
      stopped = true;
    }
  };

  cycle();

  return {
    get segments() { return index; },
    async stop() {
      stopped = true;
      if (timer) clearTimeout(timer);
      if (recorder && recorder.state === "recording") {
        await new Promise((resolve) => {
          const done = recorder.onstop;
          recorder.onstop = (ev) => { done && done(ev); resolve(); };
          try { recorder.stop(); } catch { resolve(); }
        });
      }
      await Promise.allSettled(pending);
    },
  };
}
